import apiClient from '../utils/apiClient';
import enginesService from './enginesService';

/**
 * Document signing for one facility.
 *
 * Reads, drafts, box placement, the sender's own signature, cancelling and
 * the PDFs go through the connected-service passthrough (the facility's key
 * stays on the server). Sending, reminding and emailing signed copies go to
 * this product's own routes: their answers carry each person's personal
 * link, which is emailed to that person and never reaches this browser.
 */
const SERVICE = 'esign';
const engine = (facilityId, method, path, body, config = {}) =>
  apiClient.request({
    method,
    url: `/api/engines/${SERVICE}/panel/engine/${path}`,
    params: { facility_id: facilityId, ...(config.params || {}) },
    ...(body !== undefined ? { data: body } : {}),
    ...(config.headers ? { headers: config.headers } : {}),
    ...(config.responseType ? { responseType: config.responseType } : {}),
  });
const own = (facilityId) => `/api/facilities/${facilityId}/document-signing`;
const newKey = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const documentSigningService = {
  connection: (facilityId) => enginesService.connection(SERVICE, facilityId),

  list: (facilityId, params = {}) =>
    engine(facilityId, 'get', 'envelopes', undefined, { params: { limit: 100, ...params } }),

  get: (facilityId, id) => engine(facilityId, 'get', `envelopes/${id}`),

  usage: (facilityId) => engine(facilityId, 'get', 'usage'),

  /** One draft per click: the key makes a repeated click the same request. */
  create: (facilityId, file, envelope, idemKey = newKey('create')) => {
    const form = new FormData();
    form.append('file', file);
    form.append('envelope', new Blob([JSON.stringify(envelope)], { type: 'application/json' }));
    // multipart/form-data, never the client's default application/json —
    // that makes axios serialize the FormData as JSON and the file never
    // arrives (the 2026-08-13 upload crash). Axios adds the boundary.
    return engine(facilityId, 'post', 'envelopes', form, {
      headers: { 'Content-Type': 'multipart/form-data', 'Idempotency-Key': idemKey },
    });
  },

  setFields: (facilityId, id, fields) =>
    engine(facilityId, 'put', `envelopes/${id}/fields`, { fields }),

  signAsSender: (facilityId, id, typedName) =>
    engine(facilityId, 'post', `envelopes/${id}/from-signature`, { typed_name: typedName },
      { headers: { 'Idempotency-Key': `presign-${id}` } }),

  cancel: (facilityId, id, reason) =>
    engine(facilityId, 'post', `envelopes/${id}/void`, { reason: reason || 'Cancelled by the sender.' },
      { headers: { 'Idempotency-Key': `void-${id}` } }),

  remove: (facilityId, id) => engine(facilityId, 'delete', `envelopes/${id}`),

  /** stage: source | completed | signed_<index>. Returns a Blob. */
  pdf: (facilityId, id, stage) =>
    engine(facilityId, 'get', `envelopes/${id}/pdf`, undefined,
      { params: { stage }, responseType: 'blob' }),

  send: (facilityId, id) => apiClient.post(`${own(facilityId)}/envelopes/${id}/send`),

  remind: (facilityId, id, recipientIndex) =>
    apiClient.post(`${own(facilityId)}/envelopes/${id}/remind`,
      recipientIndex === undefined || recipientIndex === null ? {} : { recipient_index: recipientIndex }),

  emailCopiesAgain: (facilityId, id) =>
    apiClient.post(`${own(facilityId)}/envelopes/${id}/copies`),
};

export default documentSigningService;
